"use client";

import React, { useState, useRef, useEffect, useId } from "react";
import ReactPlayer from "react-player";
import CustomControls from "./CustomControls";
import styles from "./VideoPlayer.module.css";

const VideoPlayer = ({
  src = "https://www.w3schools.com/html/mov_bbb.mp4",
  initialPlaybackRate = 1.0,
  autoPlay = true,
  aspectRatio = 9 / 16,
}) => {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const clickTimeoutRef = useRef(null);
  const speedControlId = useId();

  // Player state – auto‑mute enabled on load
  const [playing, setPlaying] = useState(autoPlay);
  const [muted, setMuted] = useState(true);
  const [volume, setVolume] = useState(1.0);
  const [played, setPlayed] = useState(0); // value between 0 and 1
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(initialPlaybackRate);

  // UI overlay state
  const [showControls, setShowControls] = useState(false);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const safeAspectRatio = Number.isFinite(Number(aspectRatio)) && Number(aspectRatio) > 0
    ? Number(aspectRatio)
    : 9 / 16;

  // Force re-render on window resize if needed
  const [resizeKey, setResizeKey] = useState(Date.now());
  useEffect(() => {
    const handleResize = () => setResizeKey(Date.now());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handlePlaybackShortcut = (event) => {
      if ((event.code !== "Space" && event.key !== " ") || event.repeat) return;

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('input, textarea, select, button, a, [contenteditable="true"]')
      ) {
        return;
      }

      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      if (!isVisible) return;

      event.preventDefault();
      setPlaying((current) => !current);
    };

    window.addEventListener("keydown", handlePlaybackShortcut);
    return () => window.removeEventListener("keydown", handlePlaybackShortcut);
  }, []);

  // ReactPlayer callbacks
  const handleProgress = (state) => setPlayed(state.played);
  const handleDuration = (dur) => setDuration(dur);
  const handleSeekChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setPlayed(newValue);
    if (playerRef.current) {
      playerRef.current.seekTo(newValue);
    }
  };

  const handlePlaybackRateChange = (e) => {
    setPlaybackRate(parseFloat(e.target.value));
  };

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return "00:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes < 10 ? "0" + minutes : minutes}:${secs < 10 ? "0" + secs : secs}`;
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setMuted(newVolume === 0);
  };

  // Toggle play/pause
  const togglePlay = (e) => {
    e.stopPropagation();
    setPlaying((prev) => !prev);
  };

  // Toggle mute/unmute
  const toggleMute = (e) => {
    e.stopPropagation();
    setMuted((prev) => !prev);
  };

  // Toggle fullscreen mode
  const toggleFullscreen = (e) => {
    e.stopPropagation();
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  // Differentiate between single and double clicks:
  const handleContainerClick = (e) => {
    // If a click is already pending, do nothing.
    if (clickTimeoutRef.current) return;
    clickTimeoutRef.current = setTimeout(() => {
      // Single click action: toggle the overlay controls on touch devices.
      setShowControls((prev) => !prev);
      clickTimeoutRef.current = null;
    }, 250);
  };

  const handleContainerDoubleClick = (e) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    // Double click action: trigger fullscreen
    toggleFullscreen(e);
  };

  return (
    <div
      className={styles.videoPlayerContainer}
      ref={containerRef}
      onClick={handleContainerClick}
      onDoubleClick={handleContainerDoubleClick}
      onMouseEnter={() => setShowControls(true)}
      onMouseMove={() => setShowControls(true)}
      onMouseLeave={() => playing && setShowControls(false)}
    >
      <div
        className={styles.videoWrapper}
        style={{ '--video-aspect-padding': `${100 / safeAspectRatio}%` }}
      >
        <div className={styles.reactPlayerWrapper}>
          <ReactPlayer
            key={resizeKey}
            ref={playerRef}
            url={src}
            playing={playing}
            loop={true}
            muted={muted}
            volume={volume}
            playbackRate={playbackRate}
            controls={false} // remove native controls
            width="100%"
            height="100%"
            onProgress={handleProgress}
            onDuration={handleDuration}
            className={styles.reactPlayer}
          />
        </div>

        {/* Custom Controls Overlay */}
        {(!playing || showControls) && (
          <CustomControls
            playing={playing}
            togglePlay={togglePlay}
            muted={muted}
            toggleMute={toggleMute}
            volume={volume}
            handleVolumeChange={handleVolumeChange}
            playbackRate={playbackRate}
            handlePlaybackRateChange={handlePlaybackRateChange}
            speedControlId={speedControlId}
            toggleFullscreen={toggleFullscreen}
            isFullscreen={isFullscreen}
          />
        )}

      </div>
      <div className={styles.transportControlRow} onClick={(e) => e.stopPropagation()}>
        <span className={styles.timeLabel}>{formatTime(played * duration)}</span>
        <input
          type="range"
          min={0}
          max={1}
          step="0.01"
          value={played}
          onChange={handleSeekChange}
          className={styles.progressSlider}
          style={{ '--progress': `${played * 100}%` }}
          aria-label="Video progress"
        />
        <span className={styles.timeLabel}>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default VideoPlayer;
